import { eventChannel } from 'redux-saga';
import { all, fork, take, call, put, takeLatest, select, cancel } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import { getType } from 'typesafe-actions';
import { forEach, get, groupBy, reduce, includes, map, mapKeys } from 'lodash';

import { selectCurrentUser } from '@lib/selectors';
import storage from '@lib/services/storageService';
import { sendLog, LoggerStatusEnum } from 'ui-model';
import { signOut } from '@lib/actions';
import configService from '@lib/services/configService';
// TODO refactor
import { NOTIFICATION_TYPE_CHAT, NOTIFICATION_TYPE_NOTIFICATION_CREATED } from 'components/WebNotification/constants';
import {
  SAVE_BROADCAST_SUCCESS,
  UPDATE_BROADCAST_STATUS,
} from 'modules/Broadcasts/pages/BroadcastsForm/containers/BroadcastPage/constants';
import { loadNotifications } from 'containers/Header/actions';
import request from 'utils/request';
import { initiateSocket } from 'utils/socket';
import { patientJourneyResponseInterceptor } from 'utils/interceptorsPatient';

import { selectSubAccountSettings } from 'selectors';

import {
  getBroadcastsCountError,
  getBroadcastsCountSuccess,
  getDowntimeMessageSuccess,
  getLanguagesError,
  getLanguagesSuccess,
  loadIntakeFormsSquiggliesError,
  loadIntakeFormsSquiggliesSuccess,
  loadWidgetUserError,
  loadWidgetUserSuccess,
  onSocketActivity,
  onSocketAvailability,
  onSocketBroadcastUpdate,
  onSocketChatActivity,
  onSocketHasNewActivity,
  onSocketMessage,
  onSocketOffer,
  onSocketReferralPending,
  onSocketTelehealthStatusUpdate,
  onSocketTelehealth,
  onSocketUploadAppointmentUpdate,
  onSocketUploadPatientUpdate,
  onSocketUploadRecallUpdate,
  onSocketUploadReferralUpdate,
  onSocketUploadUpdate,
  onSocketUserUpdate,
  onSocketWaitingRoomPatientCreate,
  onSocketWaitingRoomPatientStatusUpdate,
  onSocketWaitingRoomPatientUpdate,
  showWebNotification,
  webNotificationShowed,
  onSocketSetOnlineUser,
  onSocketSetOfflineUser,
  loadPatientJourneySuccess,
  loadPatientJourneyError,
} from './actions';
import {
  EMIT_SOCKET_TYPING,
  GET_BROADCASTS_COUNT,
  GET_DOWNTIME_MESSAGE,
  GET_LANGUAGES,
  LOAD_INTAKE_FORMS_SQUIGGLIES,
  LOAD_WIDGET_USER,
  REGISTER_SOCKET,
  SHOW_WEB_NOTIFICATION,
  LOAD_PATIENT_JOURNEY,
} from './constants';

export function* onWatchShowWebNotification({ data, notificationType, template }) {
  const notification = {
    type: notificationType,
    title: get(data, 'message', ''),
    data: false,
  };
  const currentUser = yield select(selectCurrentUser);
  const settings = yield select(selectSubAccountSettings);
  const unsubscribedMessages = get(settings, 'communication.unsubscribedMessages', []);
  const showNotification =
    notificationType !== NOTIFICATION_TYPE_CHAT && !includes(unsubscribedMessages, `inapp.${template}`);
  const showChatNotification =
    notificationType === NOTIFICATION_TYPE_CHAT &&
    !includes(unsubscribedMessages, 'inapp.providerNewInboundChatActivity');

  if (get(data, 'to', data.user) === currentUser._id && (showNotification || showChatNotification)) {
    notification.data = {
      ...data,
      notificationType,
    };

    if (notificationType === NOTIFICATION_TYPE_CHAT) {
      notification.title = `You have a new message from ${data.patientName}`;
    }

    yield put(webNotificationShowed(notification));
  }
}

function responseInterceptorIntakeFormsSquigglies({ tag, title }) {
  return {
    id: tag,
    name: title,
  };
}

export function* onWatchGetBroadcastsCount() {
  try {
    if (!window.location.pathname.includes('embed')) {
      const response = yield call(request, '/broadcasts/count?status=pending');

      yield put(getBroadcastsCountSuccess(response));
    }
  } catch (err) {
    yield put(getBroadcastsCountError(err));
  }
}

export function* onWatchGetDowntimeMessage() {
  try {
    const { downtimeMessage } = yield call(request, '/downtimeMessage');

    yield put(getDowntimeMessageSuccess(downtimeMessage || ''));
  } catch (err) {
    sendLog(
      'Error on App/sagas.js onWatchGetDowntimeMessage',
      { errorStack: err, context: {} },
      LoggerStatusEnum.error,
    );
  }
}

export function* onWatchGetLanguages() {
  try {
    const languages = yield call(request, '/messageTemplates/languages');
    const formattedLanguages = [];

    mapKeys(languages, (value, key) => {
      formattedLanguages.push({
        id: key,
        label: value,
      });
    });

    yield put(getLanguagesSuccess(formattedLanguages));
  } catch (err) {
    yield put(getLanguagesError(err));
  }
}

function* onWatchLoadIntakeFormsSquigglies() {
  try {
    const { response } = yield call(request, '/patientFormTemplates?lumabot=false');

    yield put(
      loadIntakeFormsSquiggliesSuccess(map(response, (data) => responseInterceptorIntakeFormsSquigglies(data))),
    );
  } catch (err) {
    yield put(loadIntakeFormsSquiggliesError(err));
  }
}

export function* onWatchLoadWidgetUser() {
  try {
    const widgetUser = yield call(request, '/widgets/user');

    yield put(loadWidgetUserSuccess(widgetUser));
  } catch (err) {
    yield put(loadWidgetUserError(err));
  }
}

export function* onWatchLocationChange(action) {
  const {
    payload: { pathname },
  } = action;
  if (!/^\/patients\//i.test(pathname) || /journey/.test(pathname)) {
    return;
  }
  // post recent patientId
  try {
    const id = pathname.split('/')[2];
    // WORKAROUND skip wrong ids in case some route interferes
    if (!id.match(/^[0-9a-fA-F]{24}$/)) {
      return;
    }
    const options = {
      method: 'post',
      body: {
        id,
      },
    };
    yield call(request, '/patients/recent', options);
  } catch (err) {
    sendLog(
      'Error on App/sagas.js onWatchLocationChange',
      { errorStack: err, context: action.payload.pathname },
      LoggerStatusEnum.error,
    );
  }
}

function onSocketSubscribe(socket) {
  return eventChannel((dispatch) => {
    function subscribeEvents(socketEvents) {
      forEach(socketEvents, (callback, name) => {
        socket.emit('subscribe', name);
        socket.on(name, (data, ackCallback) => {
          // From #86b55w16u - The expiration is already being updated on user's interactions,
          // so no need to refresh it here
          // Renew session timeout on socket events
          // storage.refreshSession();

          if (Array.isArray(callback)) {
            forEach(callback, (action) => {
              switch (name) {
                case 'luma:server:notification:create':
                  dispatch(action(data, NOTIFICATION_TYPE_NOTIFICATION_CREATED));
                  break;
                case 'chat:client:inbound:message':
                  dispatch(action(data, NOTIFICATION_TYPE_CHAT));
                  break;
                default:
                  dispatch(action(data));
              }
            });
          } else {
            dispatch(callback(data));
          }
          ackCallback?.();
        });
      });
    }

    // subscribe events
    subscribeEvents({
      'chat:client:inbound:message': [onSocketMessage, showWebNotification],
      'chat:server:chatActivity': [onSocketChatActivity],
      'chat:server:inbound:message:replyToAutomated': onSocketMessage,
      'chat:server:outbound:message': onSocketMessage,
      'chat:server:outbound:message:automated': onSocketMessage,
      'chat:server:outbound:message:mention': onSocketMessage,
      'chat:presence:online': onSocketSetOnlineUser,
      'chat:presence:offline': onSocketSetOfflineUser,
      'luma:server:activity:create': [onSocketActivity, onSocketHasNewActivity],
      'luma:server:availability:create': onSocketAvailability,
      'luma:server:availability:update ': onSocketAvailability,
      'luma:server:broadcast:update': [onSocketBroadcastUpdate],
      'luma:server:notification:create': [loadNotifications, showWebNotification],
      'luma:server:offer:create': [onSocketOffer, onSocketActivity, onSocketHasNewActivity],
      'luma:server:referral:create': onSocketReferralPending,
      'luma:server:upload:appointment:update': onSocketUploadAppointmentUpdate,
      'luma:server:upload:patient:update': onSocketUploadPatientUpdate,
      'luma:server:upload:recall:update': onSocketUploadRecallUpdate,
      'luma:server:upload:referral:update': onSocketUploadReferralUpdate,
      'luma:server:upload:increment': onSocketUploadUpdate,
      'luma:server:user:update': onSocketUserUpdate,
      'telehealth:server:patient:readiness': [onSocketTelehealthStatusUpdate, onSocketTelehealth],
      'telehealth:server:patient:waiting': [onSocketTelehealthStatusUpdate, onSocketTelehealth],
      'luma:server:telehealth:ended:updated': [onSocketTelehealthStatusUpdate, onSocketTelehealth],
      'luma:server:telehealth:created:updated': [onSocketTelehealthStatusUpdate, onSocketTelehealth],
      'luma:server:telehealth:in-progress:updated': [onSocketTelehealthStatusUpdate, onSocketTelehealth],
      'waitingRoomPatient:server:patient:created': onSocketWaitingRoomPatientCreate,
      'waitingRoomPatient:server:patient:updated': onSocketWaitingRoomPatientUpdate,
      'waitingRoomPatient:server:patient:arrived': onSocketWaitingRoomPatientStatusUpdate,
      'waitingRoomPatient:server:patient:waiting': onSocketWaitingRoomPatientStatusUpdate,
      'waitingRoomPatient:server:patient:removed': onSocketWaitingRoomPatientStatusUpdate,
    });
    return () => {};
  });
}

function* watchSocketRead(socket) {
  const channel = yield call(onSocketSubscribe, socket);
  while (true) {
    // eslint-disable-line no-constant-condition
    const action = yield take(channel);
    yield put(action);
  }
}

function* watchSocketWrite(socket) {
  while (true) {
    // eslint-disable-line no-constant-condition
    const { payload } = yield take(EMIT_SOCKET_TYPING);
    const name = 'chat:client:typing:start';
    socket.emit(name, payload);
  }
}

function* watchSocketIO(socket) {
  yield fork(watchSocketRead, socket);
  yield fork(watchSocketWrite, socket);
}

function* watchSocket() {
  while (true) {
    // eslint-disable-line no-constant-condition
    const { user } = yield take(REGISTER_SOCKET);
    sendLog(
      `Socket: connecting to ${configService.getApiSocketPath()} with user ${user._id}`,
      { user },
      LoggerStatusEnum.info,
    );
    const socket = yield call(initiateSocket, user._id);
    const task = yield fork(watchSocketIO, socket);

    // Disconnect socket and cancel Redux actions on sign out
    yield take(getType(signOut));
    socket.disconnect();
    yield cancel(task);
  }
}

function* onWatchLoadPatientJourney({ id, rangeBy, includeDetails }) {
  try {
    const endpoint = `/patients/${id}/journey?rangeBy=${rangeBy}`;
    let data = yield call(request, endpoint);
    data = patientJourneyResponseInterceptor(data, includeDetails);
    if (includeDetails) {
      // Group events by year and month
      const dataGroupedBy = {
        ...data,
        events: reduce(
          groupBy(data.response, 'year'),
          (obj, events, year) => ({
            ...obj,
            [year]: groupBy(events, 'month'),
          }),
          {},
        ),
      };

      yield put(loadPatientJourneySuccess(dataGroupedBy));
    } else {
      yield put(loadPatientJourneySuccess(data));
    }
  } catch (err) {
    sendLog(
      'Error on App/sagas.js  onWatchLoadPatientJourney',
      { errorStack: err, context: { id, rangeBy } },
      LoggerStatusEnum.error,
    );
    yield put(loadPatientJourneyError(err));
  }
}

export function* watchGetBroadcastsCount() {
  yield all([
    takeLatest(GET_BROADCASTS_COUNT, onWatchGetBroadcastsCount),
    takeLatest(SAVE_BROADCAST_SUCCESS, onWatchGetBroadcastsCount),
    takeLatest(UPDATE_BROADCAST_STATUS, onWatchGetBroadcastsCount),
  ]);
}

export function* watchGetLanguages() {
  yield takeLatest(GET_LANGUAGES, onWatchGetLanguages);
}

export function* watchGetDowntimeMessage() {
  yield takeLatest(GET_DOWNTIME_MESSAGE, onWatchGetDowntimeMessage);
}

export function* watchLoadIntakeFormsSquigglies() {
  yield takeLatest(LOAD_INTAKE_FORMS_SQUIGGLIES, onWatchLoadIntakeFormsSquigglies);
}

export function* watchLoadWidgetUser() {
  yield takeLatest(LOAD_WIDGET_USER, onWatchLoadWidgetUser);
}

export function* watchLocationChange() {
  yield takeLatest(LOCATION_CHANGE, onWatchLocationChange);
}

export function* watchSocketNotififications() {
  yield takeLatest(SHOW_WEB_NOTIFICATION, onWatchShowWebNotification);
}

function* watchLoadPatientJourney() {
  yield takeLatest(LOAD_PATIENT_JOURNEY, onWatchLoadPatientJourney);
}

// TODO replace `yield fork` by `yield all[]`
// https://github.com/redux-saga/redux-saga/issues/760
export default function* appSaga() {
  yield fork(watchGetBroadcastsCount);
  yield fork(watchGetDowntimeMessage);
  yield fork(watchGetLanguages);
  yield fork(watchLoadIntakeFormsSquigglies);
  yield fork(watchLoadWidgetUser);
  yield fork(watchLocationChange);
  yield fork(watchSocket);
  yield fork(watchSocketNotififications);
  yield fork(watchLoadPatientJourney);
}
